import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Calendar, Tag, CheckCircle } from 'lucide-react';
import { Component, Project } from '../types';
import { getComponentTypeColor, getStatusColor, getAccessibilityFeatures, formatDate } from '../utils/filters';
import ComponentTypeIcon from './ComponentTypeIcon';

interface ComponentCardProps {
  component: Component;
  project?: Project;
}

const ComponentCard: React.FC<ComponentCardProps> = ({ component, project }) => {
  const accessibilityFeatures = getAccessibilityFeatures(component);

  return (
          <Link to={`/projects/${project?.id || 'unknown'}/components/${component.name}`}>
      <div className="card hover:shadow-md transition-shadow duration-200 group p-6">


        {/* Header */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white group-hover:text-primary-600 transition-colors line-clamp-1">
              {component.name}
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">{component.name}</p>
          </div>
          
          <div className="flex items-center space-x-2 ml-2">
            <span className={`badge ${getComponentTypeColor(component.type || 'unknown')} flex items-center space-x-1`}>
              <ComponentTypeIcon type={component.type || 'unknown'} size="sm" />
              <span>{(component.type || 'unknown').replace(/^\d+-/, '')}</span>
            </span>
            <span className={`badge ${getStatusColor(component.status || 'draft')}`}>
              {component.status || 'draft'}
            </span>
          </div>
        </div>

        {/* Description */}
        {component.description && (
          <p className="text-gray-600 dark:text-gray-300 text-sm mb-3 line-clamp-2">
            {component.description}
          </p>
        )}

        {/* Tags */}
        {component.tags && component.tags.length > 0 && (
          <div className="flex items-center space-x-1 mb-3">
            <Tag className="w-3 h-3 text-gray-400" />
                          <div className="flex flex-wrap gap-1">
                {component.tags.slice(0, 3).map((tag: string) => (
                  <span
                    key={tag}
                    className="text-xs bg-gray-100 dark:bg-gray-600 text-gray-600 dark:text-gray-200 px-2 py-1 rounded"
                  >
                    {tag}
                  </span>
                ))}
                {component.tags.length > 3 && (
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    +{component.tags.length - 3} more
                  </span>
                )}
              </div>
          </div>
        )}

        {/* Accessibility Features */}
        {accessibilityFeatures.length > 0 && (
          <div className="flex items-center space-x-2 mb-3">
            <CheckCircle className="w-4 h-4 text-success-600" />
            <div className="flex flex-wrap gap-1">
              {accessibilityFeatures.slice(0, 2).map(({ feature, value }) => (
                <span
                  key={feature}
                  className={`text-xs px-2 py-1 rounded ${
                    value 
                      ? 'bg-success-100 dark:bg-success-900 text-success-800 dark:text-success-200' 
                      : 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200'
                  }`}
                >
                  A11y {feature}
                </span>
              ))}
              {accessibilityFeatures.length > 2 && (
                <span className="text-xs text-success-600 dark:text-success-400">
                  +{accessibilityFeatures.length - 2} more
                </span>
              )}
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="flex items-center justify-between pt-3 border-t border-gray-100 dark:border-gray-700">
          <div className="flex items-center space-x-4 text-xs text-gray-500 dark:text-gray-400">
            <div className="flex items-center space-x-1">
              <Calendar className="w-3 h-3" />
              <span>{formatDate(component.lastUpdated || project?.lastUpdated)}</span>
            </div>
          </div>
          
          <ArrowRight className="w-4 h-4 text-gray-400 group-hover:text-primary-600 transition-colors" />
        </div>
      </div>
    </Link>
  );
};

export default ComponentCard;
